/**
 * The release-step rail across the top of the workflow.
 *
 * A rail rather than a progress bar, and every step is reachable rather than unlocked in
 * sequence, for one reason: §15's review step has to be able to send the user back to the
 * step that fixes a problem, and a wizard that only moves forward makes that a matter of
 * pressing Back five times. The numbers still read as an order — that is what the rail is
 * for — but they are navigation, not a gate.
 *
 * THE DOT IS NOT DECORATION. A step carrying a blocking issue is marked, so "what is
 * still missing" is answerable from the rail without walking through every panel.
 * The marks come from lib/publisherValidation via the caller, so this component never
 * decides what counts as incomplete.
 *
 * No `display: none` at any width — the rail scrolls horizontally instead (see
 * .lyx-wizard-steps). Hiding a step on a phone would hide the fact that it exists.
 */
import { Check } from 'lucide-react';
import { c } from '../../styles/theme';

export interface WizardStep {
  /** 1–8. */
  number: number;
  /** Short label, e.g. "Music". */
  label: string;
}

export interface WizardStepRailProps {
  steps: readonly WizardStep[];
  /** The step being shown. */
  current: number;
  onSelect: (step: number) => void;
  /** Step numbers with at least one blocking issue. */
  blocked?: readonly number[];
  /**
   * Step numbers the user has completed a pass over.
   *
   * "Visited and left", not "correct" — correctness is `blocked`. A tick on a step that
   * still has a problem would be a false statement, so the two are combined below.
   */
  visited?: readonly number[];
}

export default function WizardStepRail({
  steps,
  current,
  onSelect,
  blocked = [],
  visited = [],
}: WizardStepRailProps) {
  return (
    <nav className="lyx-wizard-steps" aria-label="Release steps">
      {steps.map((step) => {
        const active = step.number === current;
        const hasIssue = blocked.includes(step.number);
        // A tick means: you have been here, and nothing on it is blocking. Both halves
        // are required, or the rail would congratulate a step that cannot be submitted.
        const done = !active && visited.includes(step.number) && !hasIssue;

        return (
          <button
            key={step.number}
            type="button"
            className="lyx-wizard-step"
            onClick={() => onSelect(step.number)}
            aria-current={active ? 'step' : undefined}
            aria-label={`Step ${step.number}: ${step.label}${hasIssue ? ' — needs attention' : ''}`}
            style={{
              borderColor: active ? c.accentBorder : undefined,
              background: active ? c.accentSoft : undefined,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontFamily: 'IBM Plex Mono',
                fontSize: 10,
                // The amber ring marks a step with something outstanding. Colour is
                // never the only signal — the label carries "needs attention" for
                // assistive technology, and the step's own panel lists the issue.
                background: done ? c.successSoft : hasIssue ? c.dangerSoft : c.surface3,
                color: done ? c.success : hasIssue ? c.danger : active ? c.accent : c.text2,
                border: `1px solid ${done ? c.success : hasIssue ? c.danger : 'transparent'}`,
              }}
            >
              {done ? <Check size={11} /> : step.number}
            </span>
            <span
              style={{
                fontSize: 12,
                minWidth: 0,
                textAlign: 'center',
                // text2 rather than text3 for an inactive step: it is a label somebody
                // reads to decide where to go. text3 clears neither AA nor the 3:1 floor
                // on surface1.
                color: active ? c.text1 : c.text2,
                fontWeight: active ? 500 : 400,
              }}
            >
              {step.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
